"use client"
import React from 'react';
import { useScopedI18n } from '@locales/client';
import { Button } from '@/shared/ui/form/button/button';
import { BodyTextRegularC, BodyTextMediumB, HeadingMediumH5 } from '@/shared/ui/typography2';
import * as Dialog from '@radix-ui/react-dialog';
import { StyledAlertDialogOverlay } from '@/shared/ui/alert-dialog/styles';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import {
  StyledKycVerificationDialogContent,
  Navigation,
  CloseButton,
  LogoImage,
  ButtonsContainer,
  IconWrapper,
  StatusBadge,
  StatusBadgeGlyph,
  TextContent,
} from './styles';

export type KycResultStatus = 'under_review' | 'completed'; // | 'failed' — see commented-out block below

interface KycResultModalProps {
  status: KycResultStatus;
  open: boolean;
  onClose: () => void;
  // onRetry?: () => void; // for 'failed', see below
}

const CloseGlyph = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M15.625 4.375L4.375 15.625" stroke="#AFAFAF" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15.625 15.625L4.375 4.375" stroke="#AFAFAF" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const WarningBadgeCircle = () => (
  <svg viewBox="0 0 33 33" fill="none">
    <circle cx="16.5" cy="16.5" r="15" fill="#FF9500" stroke="#201C15" strokeWidth="3" />
  </svg>
);

// const ErrorBadgeCircle = () => (
//   <svg viewBox="0 0 33 33" fill="none">
//     <circle cx="16.5" cy="16.5" r="15" fill="#D03331" stroke="#201B15" strokeWidth="3" />
//     <path d="M10.9987 22L21.9987 11" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
//     <path d="M22 22L11 11" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
//   </svg>
// );

export const KycResultModal = ({ status, open, onClose }: KycResultModalProps) => {
  const tCommon = useScopedI18n('common');
  const tVerification = useScopedI18n('verification.result_modal');

  const STATUS_CONTENT: Record<KycResultStatus, { title: string; text: string; badge: 'warning' | null }> = {
    under_review: {
      title: tVerification('states.under_review.title'),
      text: tVerification('states.under_review.text'),
      badge: 'warning',
    },
    completed: {
      title: tVerification('states.completed.title'),
      text: tVerification('states.completed.text'),
      badge: null,
    },
    // failed: {
    //   title: tVerification('states.failed.title'),
    //   text: tVerification('states.failed.text'),
    //   badge: 'error',
    // },
  };

  const { title, text, badge } = STATUS_CONTENT[status];

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onClose();
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <StyledAlertDialogOverlay />
        <StyledKycVerificationDialogContent
          aria-describedby=""
          data-item={`kyc_result_${status}`}
        >
          <VisuallyHidden>
            <Dialog.Title>{title}</Dialog.Title>
          </VisuallyHidden>

          <Navigation>
            <CloseButton onClick={onClose} aria-label={tCommon('buttons.close')}>
              <CloseGlyph />
            </CloseButton>
          </Navigation>

          <IconWrapper>
            <LogoImage src="/images/icons/ph/kyc-completed.png" alt="kyc-status" />
            {badge === 'warning' && (
              <StatusBadge $right={7}>
                <WarningBadgeCircle />
                <StatusBadgeGlyph>!</StatusBadgeGlyph>
              </StatusBadge>
            )}
            {/* {badge === 'error' && (
              <StatusBadge $right={8}>
                <ErrorBadgeCircle />
              </StatusBadge>
            )} */}
          </IconWrapper>

          <TextContent>
            <HeadingMediumH5 style={{ textAlign: 'center' }}>{title}</HeadingMediumH5>
            <BodyTextRegularC color="#AFAFAF" style={{ whiteSpace: 'pre-line' }}>
              {text}
            </BodyTextRegularC>
          </TextContent>

          <ButtonsContainer>
            {status === 'under_review' && (
              <Button
                type="button"
                onClick={onClose}
                variant="orangeGradient"
                data-analytics-id="kyc_result_under_review"
              >
                <BodyTextMediumB>{tVerification('buttons.agree')}</BodyTextMediumB>
              </Button>
            )}
            {status === 'completed' && (
              <Button
                type="button"
                onClick={onClose}
                variant="orangeGradient"
                data-analytics-id="kyc_result_completed"
              >
                <BodyTextMediumB>{tVerification('buttons.proceed')}</BodyTextMediumB>
              </Button>
            )}
            {/* {status === 'failed' && (
              <>
                <Button type="button" onClick={onRetry} variant="orangeGradient">
                  <BodyTextMediumB>{tVerification('buttons.retry_again')}</BodyTextMediumB>
                </Button>
                <Button type="button" onClick={onClose} variant="secondary">
                  <BodyTextMediumB>{tVerification('buttons.exit')}</BodyTextMediumB>
                </Button>
              </>
            )} */}
          </ButtonsContainer>
        </StyledKycVerificationDialogContent>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
